/* eslint-disable new-cap,@babel/new-cap */
// ^ The convention of FullStory is FS.

/**
 * MAKING CHANGES TO THIS FILE? Please, make sure you update the server file accordingly.
 *
 * Both files should export the same functions and variables.
 */
import type { FullStory } from '@fullstory/browser';
import { memoize } from 'lodash';

import * as user from 'js/lib/user';

declare global {
  interface Window {
    /**
     * https://developer.fullstory.com/browser/fullcapture/capture-data/#manually-delay-data-capture
     *
     * To manually delay capture, set window['_fs_capture_on_startup'] to false. When you are ready
     * to start capture, you can call FS('start').
     */
    _fs_capture_on_startup?: boolean;

    /**
     * FullStory global variable.
     */
    FS: typeof FullStory | undefined;

    /**
     * It is a callback that is called when FullStory script is loaded.
     */
    _fs_on_load?: () => void;
  }
}

type StartOptions = {
  disableForCourserians?: boolean;
};

// Don't have much control over the FullStory global variable naming.
// eslint-disable-next-line camelcase
window._fs_capture_on_startup = false;
// ^ We don't want to start recording sessions for everybody, we want to avoid Courserians or other kind of
// superusers.

/**
 * It returns a promise that resolves when the FullStory script is loaded.
 * The function is memoized now so we don't inject the script multiple times.
 * In the future, we won't inject the script in here, but through GTM.
 */
const load = memoize(async (): Promise<typeof FullStory> => {
  // Set the promise to resolve when the FullStory script is loaded.
  const loader = new Promise<void>((ok) => {
    window._fs_on_load = ok;
  });

  // In the meantime, we inject the FullStory script in the head of the document so we can test.
  // We modified the script to call the _fs_on_load callback when the script is loaded.
  const script = document.createElement('script');
  script.innerHTML = `
    window['_fs_host'] = 'fullstory.com';
    window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
    window['_fs_org'] = 'ARGC0';
    window['_fs_namespace'] = 'FS';
    !function(m,n,e,t,l,x,o,g,y){var s,f,a=function(h){
    return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
    ;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
    d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
    then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
    h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
    ;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
    "function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
    (o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onload=function(){},o.onerror=function(){y("Error loading "+l)}
    ;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
    function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
    uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
    name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
    g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
    f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
    }(window,document,window._fs_namespace,"script",window._fs_script,window._fs_on_load);
  `;
  document.head.appendChild(script);
  // ^ This block will be deleted when FullStory is injected through GTM

  // Check if the script has not been loaded yet.
  if (!window.FS) {
    await loader;
  }

  // Otherwise, return FS immediately;
  return window.FS!;
});

/**
 * @deprecated
 * There is a single use of this function in the codebase.
 *
 * It sends an event to FullStory.
 */
async function event(eventName: string, data: object) {
  const FS = await load();

  FS('trackEvent', {
    name: eventName,
    properties: data,
  });
}

/**
 * It sets the FullStory properties for the user.
 * @param value - The properties to set for the user.
 */
async function set(value: object) {
  const FS = await load();

  FS('setProperties', { properties: value, type: 'user' });
}

/**
 * It starts the FullStory session.
 * @param disableForCourserians - If true, it will disable FullStory for Courserians. Default is true.
 */
async function start({ disableForCourserians = true }: StartOptions = {}) {
  // Short circuit if the user is a Courserian.
  if (disableForCourserians && user.isCourserian()) return;

  const FS = await load();

  FS('start');

  FS('setIdentity', {
    uid: user.get().external_id,
  });
}

/**
 * It stops the FullStory session.
 */
async function stop() {
  const FS = await load();

  FS('shutdown');
}

export { event, set, start, stop };
